import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { apiClient } from "@/lib/api";
import { describe } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import type { Person, PersonPool } from "@tilt/admin-sdk";

import { ModalOverlay } from "./ModalOverlay";
import { RoleSelect } from "./RoleSelect";
import { WorkplacePicker, type WorkplacePickerValue } from "./WorkplacePicker";
import { EMPTY_WORKPLACE } from "./workplace-picker-constants";

/**
 * Pool-aware "give this person access to a workplace" dialog.
 *
 * Used from the unified People page on every row. Wraps
 * apiClient.createGrant with WorkplacePicker (no UUID inputs) and
 * RoleSelect (filters by pool + scope_kind). Covers both admin and
 * pos populations from one component because the API does too.
 *
 * Constraints surfaced to the user:
 *   - POS pool, partner-anchored: scope can be partner / org / loc
 *   - POS pool, org-anchored: scope is pinned to the anchor org +
 *     loc only (org-anchored users can't grant on a sibling org)
 *   - Admin pool: any scope kind allowed; partner roles need partner
 *     scope, org/loc roles need org/loc scope (RoleSelect filters)
 *
 * Caller passes the target Person; we never refetch them.
 */
export function GrantAccessDialog({
  person,
  onClose,
  onCreated,
}: {
  readonly person: Person;
  readonly onClose: () => void;
  readonly onCreated: () => void;
}) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<WorkplacePickerValue>(EMPTY_WORKPLACE);
  const [roleSlug, setRoleSlug] = useState("");
  // Target pool - defaults to the person's own pool. Admin-only
  // persons can target POS via the bridge path (#156); flipping the
  // toggle clears the in-progress role choice because the role
  // catalogs differ.
  const [targetPool, setTargetPool] = useState<PersonPool>(person.pool);

  // POS rows surface the anchor; we use it to constrain the scope
  // picker. Org-anchored people get the org id pinned. Partner-
  // anchored people get the partner subtree available - same rule
  // pos_users handler enforces server-side.
  const isPosOrgAnchored =
    targetPool === "pos" && person.pool === "pos" && person.anchor?.kind === "organization";
  const allowedKinds = (() => {
    if (targetPool === "admin") return ["partner", "organization", "location"] as const;
    // POS target - anchor only constrains when the source row already
    // has a POS anchor. Cross-pool bridge from an admin row has no
    // pre-existing POS anchor, so the picker covers everything the
    // caller can write to.
    if (person.pool !== "pos") return ["partner", "organization", "location"] as const;
    if (person.anchor?.kind === "partner") return ["partner", "organization", "location"] as const;
    return ["organization", "location"] as const;
  })();
  const isCrossPool = targetPool === "pos" && person.pool === "admin";

  const mutate = useMutation({
    mutationFn: async () => {
      if (!scope.kind || !scope.id || !roleSlug) {
        throw new Error("Pick a workplace and a role.");
      }
      return apiClient.createGrant({
        pool: targetPool,
        person_id: person.id,
        scope: { kind: scope.kind, id: scope.id },
        role_slug: roleSlug,
        ...(isCrossPool ? { bridge_from_admin: true } : {}),
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["people"] });
      onCreated();
    },
  });

  const submitDisabled = !scope.kind || !scope.id || !roleSlug;
  const personLabel =
    [person.first_name, person.last_name].filter(Boolean).join(" ") || person.email;

  // Admin-only persons can target POS via the bridge - flipping the
  // toggle resets scope + role because the picker constraints + role
  // catalog are different.
  const setTargetPoolAndReset = (next: PersonPool): void => {
    if (next === targetPool) return;
    setTargetPool(next);
    setScope(EMPTY_WORKPLACE);
    setRoleSlug("");
  };

  return (
    <ModalOverlay ariaLabel={`Grant access for ${personLabel}`} onClose={onClose}>
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-white p-6 shadow-xl">
        <div>
          <h3 className="text-base font-semibold text-slate-900">Grant access - {personLabel}</h3>
          <p className="mt-1 text-xs text-slate-500">
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium uppercase tracking-wide">
              {person.pool}
            </span>{" "}
            {person.email}
            {isPosOrgAnchored && person.anchor?.organization_name && (
              <>
                {" • anchored at "}
                <span className="font-medium">{person.anchor.organization_name}</span>
              </>
            )}
            {person.pool === "pos" && person.anchor?.kind === "partner" && (
              <>
                {" • anchored at "}
                <span className="font-medium">{person.anchor.partner_name}</span>
              </>
            )}
          </p>
        </div>

        {/* Cross-pool target picker - only surfaced when the person is
            admin-only. For pos-pool persons we don't expose the toggle:
            granting a pos person admin-console access is a separate
            flow (the operator goes through Add Person → admin pool
            with the same email, not from this dialog). */}
        {person.pool === "admin" && (
          <fieldset className="space-y-1.5">
            <legend className="text-xs font-semibold uppercase text-slate-500">
              Grant access to
            </legend>
            <div
              role="radiogroup"
              aria-label="Target application"
              className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs"
            >
              {(["admin", "pos"] as const).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="radio"
                  aria-checked={targetPool === p}
                  onClick={() => setTargetPoolAndReset(p)}
                  className={cn(
                    "rounded px-3 py-1 font-medium transition-colors",
                    targetPool === p
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-700",
                  )}
                >
                  {p === "admin" ? "Admin console" : "POS"}
                </button>
              ))}
            </div>
            {isCrossPool && (
              <p className="text-[11px] text-slate-500">
                We'll provision a POS account for this person at the picked workplace and email them
                sign-in instructions. Their admin console access stays unchanged.
              </p>
            )}
          </fieldset>
        )}

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase text-slate-500">Workplace</legend>
          <p className="text-xs text-slate-500">
            Pick where this role applies. Partners contain organizations; organizations contain
            locations.
          </p>
          <WorkplacePicker
            value={scope}
            onChange={setScope}
            allowedKinds={allowedKinds}
            {...(isPosOrgAnchored && person.anchor?.organization_id
              ? {
                  boundOrganizationId: person.anchor.organization_id,
                  ...(person.anchor.organization_name
                    ? { boundOrganizationName: person.anchor.organization_name }
                    : {}),
                }
              : {})}
            idPrefix="grant-scope"
          />
        </fieldset>

        <fieldset className="space-y-2">
          <legend className="text-xs font-semibold uppercase text-slate-500">Role</legend>
          {/* Hide super_admin from per-partner / per-scope grants -
              it's the platform-tier role, only reachable from the
              People page's Tier=Platform invite path. Granting it
              here would force a partner-scope row that the API
              rejects anyway. */}
          <RoleSelect
            pool={targetPool}
            scopeKind={scope.kind}
            value={roleSlug}
            onChange={setRoleSlug}
            id="grant-role"
            hideSuperAdmin
          />
        </fieldset>

        {mutate.isError && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {describe(mutate.error)}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={mutate.isPending || submitDisabled}
            onClick={() => mutate.mutate()}
            className="rounded bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
          >
            {mutate.isPending ? "Granting…" : "Grant access"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}
